/**
 * Guia — "Tudo que o MGDLead mede", "O que roda sozinho" e "Dúvidas frequentes".
 *
 * As métricas são a TRADUÇÃO para gente de `docs/product/metricas.md` — sem fórmula técnica.
 * Onde a tela já tem uma frase de ajuda (`METRICA` em `analytics-ui.ts`), ela é a fonte: o
 * guia repete a mesma explicação que o (?) da tela mostra, nunca outra.
 */
import { Gauge, HelpCircle, Workflow } from 'lucide-react';

import { METRICA, type MetricaId } from '../../pages/workspace/dashboard/analytics-ui';
import { img } from './imagens';
import type { CapituloDoGuia, PassoDoGuia } from './tipos';

interface DefMetrica {
  id: string;
  /** Usa o rótulo e a ajuda da tela quando a métrica está no catálogo do painel. */
  catalogo?: MetricaId;
  titulo?: string;
  oQueE: string;
  comoLer: string;
  seRuim: string;
  onde: string;
}

function metrica(d: DefMetrica): PassoDoGuia {
  const spec = d.catalogo ? METRICA[d.catalogo] : undefined;
  const ajuda = spec && 'ajuda' in spec ? spec.ajuda : undefined;
  return {
    id: d.id,
    titulo: d.titulo ?? spec?.label ?? d.id,
    onde: d.onde,
    texto: ajuda ? `${d.oQueE}\n\n${ajuda}` : d.oQueE,
    blocos: [
      { rotulo: 'Como ler', texto: d.comoLer },
      { rotulo: 'Se estiver ruim', texto: d.seRuim },
    ],
  };
}

export const METRICAS: CapituloDoGuia = {
  id: 'metricas',
  titulo: 'Tudo que o MGDLead mede',
  resumo:
    'Cada número do painel: o que é, como ler e o que fazer quando ele está ruim. Sem fórmula — com o raciocínio.',
  icone: Gauge,
  capa: img('dashboard-indicadores', 'Os indicadores do Dashboard', [
    'Saldo no período',
    'Taxa de entrada',
    'Saídas: só quem saiu por conta própria',
  ]),
  secoes: [
    {
      id: 'movimento',
      titulo: 'Movimento dos grupos',
      resumo: 'Quem entrou, quem saiu e quanto ainda cabe.',
      passos: [
        metrica({
          id: 'entradas',
          catalogo: 'entradas',
          onde: 'Dashboard · Campanhas · Gestão',
          oQueE:
            'Quantas pessoas entraram nos grupos no período, contadas pelo aviso do WhatsApp na hora da entrada.',
          comoLer:
            'Compare com o período anterior (a seta ao lado). Quem já estava no grupo quando você começou a monitorar não conta: a primeira varredura não é entrada.',
          seRuim:
            'Entradas caindo com cliques estáveis = problema depois do clique (grupo cheio, convite quebrado). Com cliques caindo junto = problema no anúncio ou na página.',
        }),
        metrica({
          id: 'saidas',
          catalogo: 'saidas',
          onde: 'Dashboard · Gestão',
          oQueE: 'Quantas pessoas saíram por conta própria.',
          comoLer:
            'Remoção feita por administrador fica em outra linha ("você removeu"): limpar spam não é evasão e não pode pesar na conta da campanha.',
          seRuim:
            'Muita saída logo após entrar costuma ser promessa do anúncio diferente do que o grupo entrega, ou mensagens demais. Veja a Evasão por anúncio na página da campanha.',
        }),
        metrica({
          id: 'saldo',
          catalogo: 'saldo',
          onde: 'Dashboard',
          oQueE: 'Entradas menos saídas: quanto os seus grupos cresceram no período.',
          comoLer:
            'É o número de topo do dia. Positivo, os grupos cresceram; negativo, encolheram.',
          seRuim:
            'Saldo negativo com tráfego rodando: olhe primeiro a evasão (gente saindo) e depois as entradas (gente não chegando).',
        }),
        metrica({
          id: 'sem-causa',
          titulo: 'Saídas sem causa conhecida e saídas órfãs',
          onde: 'Dashboard · Gestão',
          oQueE:
            'Sem causa: o WhatsApp avisou a saída mas não disse se foi a pessoa ou um admin. Órfã: a pessoa saiu e nunca vimos ela entrar (estava lá antes do monitoramento, ou entrou com o número fora do ar).',
          comoLer:
            'Elas têm linha própria de propósito: misturá-las com as saídas inventaria evasão que não houve.',
          seRuim:
            'Muitas órfãs de repente quase sempre são um período com o número desconectado. Use dois números por grupo.',
        }),
        metrica({
          id: 'pessoas',
          titulo: 'Pessoas nos grupos, vagas e lotação',
          onde: 'Dashboard · Grupos · Campanhas',
          oQueE:
            'Quantas pessoas há em cada grupo agora, e quanto falta para o limite de segurança (perto de 980, não 1.024). Lotação é essa conta em porcentagem.',
          comoLer:
            'Um grupo a 980 de 1.024 já está "100%": saiu do rodízio do link. Na campanha, a lotação soma os grupos do bloco.',
          seRuim:
            'Bloco acima de 85%: crie e vincule um grupo novo antes de o link ficar sem destino. Incidentes avisa com a estimativa de quando acaba.',
        }),
        metrica({
          id: 'admins',
          titulo: 'Administradores × membros',
          onde: 'Grupos · tela do grupo',
          oQueE:
            'Quantos administradores e quantos membros comuns há no grupo. Membros não inclui os administradores.',
          comoLer:
            'Só aparece depois da varredura: antes dela sabemos QUANTOS são, não QUEM — e a tela diz isso em vez de mostrar "0 admins".',
          seRuim:
            'Grupo com um admin só é grupo que trava se esse número cair. Tenha pelo menos dois.',
        }),
      ],
    },
    {
      id: 'caminho',
      titulo: 'Do anúncio ao grupo',
      resumo: 'Onde as pessoas ficam pelo caminho.',
      passos: [
        metrica({
          id: 'visitas',
          catalogo: 'visitasPagina',
          onde: 'Dashboard · Campanhas',
          oQueE: 'Quantas vezes a sua página de ofertas abriu, segundo a Meta.',
          comoLer:
            'É o começo do caminho. Precisa de campanha da Meta vinculada a um bloco e do pixel na página.',
          seRuim: 'Muito clique no anúncio e pouca visita = página lenta ou pesada no celular.',
        }),
        metrica({
          id: 'cliques-pagina',
          catalogo: 'taxaCliquePagina',
          onde: 'Dashboard · Campanhas (Página → clique)',
          oQueE: 'De cada 100 visitas na página, quantas tocaram no botão do grupo.',
          comoLer: 'É a nota da PÁGINA, não do anúncio.',
          seRuim:
            'Botão escondido, texto confuso ou página que demora: mexa na página. Teste com e sem página de passagem (Redirecionadores › Página).',
        }),
        metrica({
          id: 'cliques',
          catalogo: 'cliques',
          titulo: 'Cliques e visitantes distintos',
          onde: 'Dashboard · Redirecionadores',
          oQueE:
            'Cliques: quantas vezes o seu link foi aberto. Visitantes distintos: quantas PESSOAS — quem clicou duas vezes conta uma.',
          comoLer:
            'Não confunda com "cliques no anúncio" da Meta: aquele conta clique em qualquer lugar do anúncio, este só quem chegou no seu link. Prévias de robô (WhatsApp, Facebook montando o cartão do link) não contam.',
          seRuim:
            'Cliques do link muito abaixo dos cliques no anúncio = a página está perdendo gente antes do botão.',
        }),
        metrica({
          id: 'taxa-entrada',
          catalogo: 'taxaEntrada',
          onde: 'Dashboard · Campanhas · Gestão',
          oQueE: 'De cada 100 pessoas que clicaram no link, quantas entraram no grupo.',
          comoLer:
            'É uma estimativa por baixo: o WhatsApp não diz quem veio de qual clique, e o MGDLead casa clique e entrada pelo horário e pelo grupo — só quando a confiança é boa.',
          seRuim:
            'Taxa caindo: grupo cheio, convite quebrado, ou o grupo pedindo aprovação para entrar. Olhe Incidentes.',
        }),
        metrica({
          id: 'cobertura',
          catalogo: 'cobertura',
          onde: 'Dashboard',
          oQueE: 'De quanto das entradas a gente sabe a origem.',
          comoLer: 'É saúde do SISTEMA, não desempenho de campanha. Acima de 80% é instalação boa.',
          seRuim:
            'Cobertura baixa: botão da página com link fixo (sem o trecho inteiro), anúncio sem os parâmetros de URL, ou gente entrando por link de convite que circula por fora.',
        }),
        metrica({
          id: 'sem-origem',
          titulo: 'Cliques por origem: de anúncio, marcado à mão, sem origem',
          onde: 'Dashboard (Cobertura) · Redirecionadores',
          oQueE:
            'De anúncio: o clique trouxe a campanha da Meta. Marcado à mão: veio com utm que você escreveu (bio, story). Sem origem: chegou sem nada.',
          comoLer: '"Sem origem" não quer dizer orgânico: quer dizer que não sabemos.',
          seRuim:
            'Muito "sem origem" com anúncio rodando: refaça a instalação do link e os parâmetros de URL (capítulo Conexões).',
        }),
      ],
    },
    {
      id: 'custo',
      titulo: 'Custo',
      resumo: 'Quanto custou cada pessoa — e cada pessoa que ficou.',
      passos: [
        metrica({
          id: 'investimento',
          titulo: 'Investimento e orçamento de hoje',
          onde: 'Campanhas · Gestão · Meta Ads',
          oQueE:
            'Quanto a Meta cobrou das campanhas ligadas a cada bloco, e o orçamento diário configurado lá.',
          comoLer:
            'É um espelho da Meta, lido a cada 10 minutos. Os últimos 3 dias ainda podem mudar: a Meta corrige os números depois. Se divergir do Gerenciador, a verdade é o Gerenciador.',
          seRuim:
            'Gasto zerado com anúncio no ar: token vencido ou campanha da Meta sem vínculo com o bloco.',
        }),
        metrica({
          id: 'cpl-meta',
          titulo: 'CPL Meta',
          onde: 'Campanhas · Dashboard',
          oQueE: 'Gasto dividido pelos resultados que a própria Meta conta.',
          comoLer:
            'É o número que o Gerenciador mostra. Serve de referência, mas a Meta não sabe quem entrou no grupo.',
          seRuim:
            'CPL Meta bom e CPL real ruim = a pessoa clica e não entra (ou entra e sai). O problema está depois do anúncio.',
        }),
        metrica({
          id: 'cpl-entrada',
          titulo: 'CPL entrada e CPL real',
          onde: 'Campanhas · Gestão',
          oQueE:
            'Quanto custou cada pessoa que entrou no grupo. "Entrada" conta quem casamos com um clique; "real" soma também quem entrou com o WhatsApp desconectado (achado na reconexão).',
          comoLer:
            'É o número principal do produto. Só entra no custo a campanha da Meta VINCULADA ao bloco — entrada vinda de campanha não vinculada aparece "fora do custo".',
          seRuim:
            'CPL real subindo: compare a taxa de entrada (gente que não entra) e a evasão do 1º dia (gente que entra e sai). Mexa no que piorou.',
        }),
        metrica({
          id: 'custo-ficou',
          titulo: 'Custo por quem ficou',
          onde: 'Campanhas (Custo/ficou 7d) · Gestão',
          oQueE:
            'Gasto dividido por quem entrou e continuava no grupo depois de 7 dias (ou no fim do dia, na Gestão).',
          comoLer:
            'É o "CPL de verdade" do gestor de tráfego: uma campanha barata que traz gente que sai logo sai cara aqui.',
          seRuim:
            'Corte o anúncio com custo por quem ficou alto, mesmo que o CPL de entrada pareça bom.',
        }),
        metrica({
          id: 'meta-razoes',
          titulo: 'Custo por clique, CPM e CTR (Meta Ads)',
          onde: 'Meta Ads',
          oQueE:
            'Razões calculadas sobre os números da Meta: custo por clique no anúncio, custo por mil impressões e taxa de clique.',
          comoLer: 'Sem impressão ou sem clique, aparece "—", não zero.',
          seRuim: 'CPM alto = público caro ou saturado. CTR baixo = criativo que não chama.',
        }),
      ],
    },
    {
      id: 'permanencia',
      titulo: 'Permanência e evasão',
      resumo: 'Quanto do tráfego pago continua no grupo.',
      passos: [
        metrica({
          id: 'ficaram',
          titulo: 'Ficaram hoje e ficaram 24h',
          onde: 'Dashboard · Campanhas',
          oQueE:
            'Ficaram hoje: de quem entrou hoje, quantos ainda estão. Ficaram 24h: de quem já completou 24 horas no grupo, quantos não saíram.',
          comoLer:
            'Os mais novos ainda estão "aguardando 24h" e não entram na conta — senão todo número pareceria ótimo.',
          seRuim:
            'Abaixo do normal da sua operação: veja quais anúncios trazem quem sai (página da campanha › Por anúncio).',
        }),
        metrica({
          id: 'evasao',
          catalogo: 'evasao',
          titulo: 'Evasão (D0, D1, D7, D30…)',
          onde: 'Dashboard (Evasão) · Campanhas · Histórico',
          oQueE:
            'Da turma que entrou num dia, quantos saíram por conta própria até o 1º, 3º, 7º, 30º dia.',
          comoLer:
            'Cada coluna é uma turma. Marco que ainda não chegou aparece como "—" (a turma de anteontem ainda não teve 7 dias). O semáforo compara com a sua meta, ou com o normal dos últimos 28 dias.',
          seRuim:
            'Evasão do 1º dia alta é o sinal mais rápido de criativo ruim: o anúncio promete uma coisa e o grupo é outra.',
        }),
        metrica({
          id: 'permanencia',
          catalogo: 'meiaVida',
          onde: 'Dashboard',
          oQueE:
            'Quanto tempo as pessoas ficam no grupo antes de sair — a mediana, entre as que já saíram.',
          comoLer:
            'Quem já estava antes do monitoramento tem permanência DESCONHECIDA e não entra na conta, nem como zero. A tela mostra quantas são.',
          seRuim: 'Mediana curta: revise frequência e qualidade das mensagens do grupo.',
        }),
      ],
    },
    {
      id: 'dinheiro',
      titulo: 'Dinheiro de afiliado',
      passos: [
        metrica({
          id: 'lucro-lead',
          titulo: 'Lucro por lead',
          onde: 'Dashboard · Shopee',
          oQueE: 'A comissão do dia dividida pelas pessoas nos grupos: quanto cada pessoa rendeu.',
          comoLer:
            'As faixas (baixo, normal, ideal, muito bom) dão a régua. Dia sem lançamento aparece "—", não R$ 0,00.',
          seRuim:
            'Lucro por lead caindo com grupo crescendo: as ofertas não acompanham o público. Compare por bloco (Sub ID).',
        }),
      ],
    },
    {
      id: 'saude',
      titulo: 'Saúde da medição',
      passos: [
        metrica({
          id: 'captura',
          titulo: 'Captura do monitorador e "atualizada há…"',
          onde: 'Monitoradores · Dashboard',
          oQueE:
            'Capturando / Instável / Sem captura: se o número está entregando os avisos do WhatsApp. "WhatsApp ao vivo · último evento há…" e "Meta atualizada há…" dizem a idade do dado.',
          comoLer:
            'Dado velho vem sempre com a idade junto: nunca confunda "não chegou nada" com "não aconteceu nada".',
          seRuim:
            'Sem captura: reconecte o número. Meta parada há mais de 30 minutos: teste a conexão em Meta Ads.',
        }),
        metrica({
          id: 'regiao',
          titulo: 'De onde é o seu público',
          onde: 'Dashboard',
          oQueE: 'A divisão das pessoas por estado e por DDD, pelo telefone de cada uma.',
          comoLer:
            'A porcentagem é sobre quem tem telefone brasileiro. Quem só tem o identificador do WhatsApp entra em "sem DDD".',
          seRuim:
            'Público concentrado onde você não anuncia de propósito? Vale um conjunto separado para essa região.',
        }),
      ],
    },
  ],
};

/* ========================================================================== */

export const AUTOMATICO: CapituloDoGuia = {
  id: 'o-que-roda-sozinho',
  titulo: 'O que roda sozinho',
  resumo: 'Tudo que o MGDLead faz por você sem ninguém pedir — e com que frequência.',
  icone: Workflow,
  secoes: [
    {
      id: 'tabela',
      titulo: 'O que, quando e para quê',
      passos: [
        {
          id: 'tempo-real',
          titulo: 'Na hora',
          texto: 'O que acontece no instante em que acontece.',
          itens: [
            'Entradas e saídas dos grupos — o WhatsApp avisa o painel na hora.',
            'Cliques no seu link — registra a origem (campanha, conjunto, anúncio) e escolhe o grupo.',
            'Grupo cheio — a cada clique: ao bater o limite de segurança, o próximo da fila assume.',
            'Número de reserva — se o principal cair, o outro número dentro do grupo assume.',
          ],
        },
        {
          id: 'periodico',
          titulo: 'De tempos em tempos',
          texto: 'O que roda em ciclo, sem precisar de botão.',
          itens: [
            'Gasto da Meta — a cada 10 minutos (e "Sincronizar agora" quando você quiser).',
            'Ligar clique ↔ entrada — a cada 10 minutos, marcando também cliques repetidos e suspeitos.',
            'Número fora do ar — a cada 5 minutos; avisa em Incidentes, por e-mail e por WhatsApp.',
            'Convite dos grupos — conferido periodicamente; convite revogado ou de outro grupo vira incidente.',
            'Contagem dos grupos — conferida todo dia contra quem sabemos que está lá.',
            'Agregados do painel — recalculados a partir dos eventos, para os números baterem com o histórico.',
            'Comissão da Shopee — importada sozinha quando a conta está conectada.',
          ],
        },
        {
          id: 'na-acao',
          titulo: 'Quando você faz algo',
          texto: 'Consequências automáticas das suas ações.',
          itens: [
            'Primeira varredura — ao escolher um grupo: descobre quem já estava lá (sem contar como entrada).',
            'Busca do convite — ao vincular o grupo à campanha.',
            'Histórico da Meta — na primeira leitura, traz os últimos 30 dias.',
            'Reconexão — ao reconectar um número, refaz a varredura e conta quem entrou enquanto ele esteve fora ("sem horário").',
          ],
          notas: [
            {
              tom: 'sozinho',
              titulo: 'Por que não "tudo a cada minuto"',
              texto:
                'WhatsApp e cliques já são em tempo real. Consultar a Meta ou o WhatsApp a cada minuto não traria dado novo (a Meta atualiza o gasto com 15–30 minutos de atraso) e aumentaria o risco de bloqueio do número e da conta de anúncios.',
            },
          ],
        },
      ],
    },
  ],
};

/* ========================================================================== */

function faq(id: string, titulo: string, texto: string): PassoDoGuia {
  return { id, titulo, texto };
}

export const DUVIDAS: CapituloDoGuia = {
  id: 'duvidas',
  titulo: 'Dúvidas frequentes',
  resumo: 'As perguntas que mais chegam no suporte, respondidas direto.',
  icone: HelpCircle,
  secoes: [
    {
      id: 'numeros',
      titulo: 'Sobre os números',
      passos: [
        faq(
          'cpl-traco',
          'O custo por entrada aparece "—".',
          'Falta uma de três coisas: a campanha da Meta ligada ao bloco (Campanhas › ⋯ › Meta Ads › Vincular), os parâmetros de URL no anúncio com o trecho inteiro na página, ou entradas no período.',
        ),
        faq(
          'sem-origem',
          'Os cliques aparecem como "sem origem".',
          'O botão da página está com um link fixo em vez do trecho completo, ou o anúncio está sem os parâmetros de URL. Refaça a instalação (capítulo Conexões) e clique de novo para conferir.',
        ),
        faq(
          'nao-bate-whatsapp',
          'O número do painel não bate com o que eu vejo no WhatsApp.',
          'O painel conta a partir do momento em que o número começou a monitorar, e só enquanto ele está conectado. Use "Sincronizar pessoas" no grupo para recontar quem está lá agora.',
        ),
        faq(
          'cpl-real-menor',
          'O CPL real ficou menor que o da Meta. Pode?',
          'Desconfie de campanha duplicada na Meta sem vínculo: as entradas dela chegam e o gasto fica de fora. O aviso amarelo "campanha da Meta sem bloco" aponta qual é — vincule a cópia ao bloco.',
        ),
        faq(
          'meta-diferente',
          'O gasto difere do Gerenciador de Anúncios.',
          'Os últimos 3 dias ainda mudam: a Meta reprocessa. Espere fechar, ou toque em "Sincronizar agora". Se continuar diferente, a verdade é o Gerenciador — e vale avisar o suporte.',
        ),
      ],
    },
    {
      id: 'whatsapp',
      titulo: 'Sobre o WhatsApp',
      passos: [
        faq(
          'caiu',
          'O número caiu e eu reconectei. Por que faltou gente?',
          'Com o número fora do ar, entradas e saídas não chegam e não são recuperáveis com horário. A reconexão acha quem entrou e conta como "sem horário". A defesa é ter dois números em cada grupo.',
        ),
        faq(
          'desconectar',
          'Posso desconectar pelo celular?',
          'Não: em "Aparelhos conectados" a sessão cai sem o painel saber. Use "Desconectar" no menu do número, na tela de Monitoradores.',
        ),
        faq(
          'admin',
          'O número monitorador precisa ser administrador?',
          'Para ver entradas e saídas, não. Para o painel buscar o link de convite do grupo sozinho, sim — por isso vale ter um número administrador conectado.',
        ),
        faq(
          'excluir-numero',
          'Se eu excluir um número, perco o histórico?',
          'Não. Arquive o número e depois, em "Mostrar arquivados", use "Excluir": ele some da lista, mas as entradas e saídas que registrou continuam nos relatórios.',
        ),
      ],
    },
    {
      id: 'organizacao',
      titulo: 'Sobre a organização',
      passos: [
        faq(
          'blocos',
          'Tenho grupos antigos e um bloco novo. Como vejo separado?',
          'Cada bloco é uma campanha com os seus grupos e as suas campanhas da Meta — a Gestão e a árvore de custo de Campanhas mostram um por linha.',
        ),
        faq(
          'varias-meta',
          'Um bloco pode receber várias campanhas da Meta?',
          'Pode — testes de criativo e de público. O painel soma o gasto delas e mostra cada uma separada ao abrir a seta do bloco.',
        ),
        faq(
          'equipe',
          'Quem da equipe pode mexer em quê?',
          'Veja "Os quatro papéis" no capítulo Configurações e equipe. Resumo: Membro só vê; Analista cuida do dia a dia; Administrador opera tudo menos dinheiro; o Proprietário mexe em dinheiro e no plano.',
        ),
      ],
    },
  ],
};
